import { GetAbilitySelection, Perk, PERKS, PerkType } from "../constants/perks";
import { SPGameState } from "../enum/GameState";
import { SoundByte } from "../enum/sounds";
import Engine from "../lib/Engine";
import { Input, KeyCode } from "../lib/Input";
import MonoBehavior from "../lib/MonoBehavior";
import { Time } from "../lib/Time";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import { clamp, clamp01, FitRectToScreen } from "../util";
import Color from "../util/Color";
import { Shapes } from "../util/Shapes";
import DialogueManager from "./DialogueManager";
import SoundManager from "./SoundManager";
import SPPlayer from "./SPPlayer";
import SPSpawnManager from "./SPSpawnManager";

export default class SPGameManager extends MonoBehavior {

    static Singleton: SPGameManager;

    static sliders: Record<SPGameState, {
        position: Vector2;
        label: string;
        get: () => number;
        set: (value: any) => void;
    }[]> = {
        [SPGameState.AbilityPick]: [],
        [SPGameState.GameOver]: [],
        [SPGameState.HowToPlay]: [
            {
                position: new Vector2(0.1, 0.9),
                label: "Master Volume",
                get: () => {
                    return SoundManager.Singleton.masterVolume;
                },
                set: value => {
                    SoundManager.Singleton.masterVolume = value;
                    if (SPGameManager.Singleton.bgMusic) SPGameManager.Singleton.bgMusic.volume = SoundManager.Singleton.musicVolume * value;
                    localStorage.setItem("fVolumeMaster", SoundManager.Singleton.masterVolume.toString());
                }
            },
            {
                position: new Vector2(0.22, 0.9),
                label: "Music Volume",
                get: () => {
                    return SoundManager.Singleton.musicVolume;
                },
                set: value => {
                    SoundManager.Singleton.musicVolume = value;
                    if (SPGameManager.Singleton.bgMusic) SPGameManager.Singleton.bgMusic.volume = SoundManager.Singleton.masterVolume * value;
                    localStorage.setItem("fVolumeMusic", SoundManager.Singleton.musicVolume.toString());
                }
            },
            {
                position: new Vector2(0.34, 0.9),
                label: "SFX Volume",
                get: () => {
                    return SoundManager.Singleton.sfxVolume;
                },
                set: value => {
                    SoundManager.Singleton.sfxVolume = value;
                    localStorage.setItem("fVolumeSfx", SoundManager.Singleton.sfxVolume.toString());
                }
            },
        ],
        [SPGameState.InGame]: [],
        [SPGameState.Title]: [],
        [SPGameState.Pause]: [], 
    };

    static buttons: Record<SPGameState, {
        shape: Shapes.Rect;
        text: string;
        callback: (engine?: Engine) => void;
    }[]> = {
        [SPGameState.Title]: [
            {
                shape: {
                    center: new Vector2(0.5, 0.9),
                    size: new Vector2(64, 16)
                },
                text: "Start Game",
                callback: () => {
                    SPGameManager.Singleton.StartGame();
                }
            },
            {
                shape: {
                    center: new Vector2(0.5, 0.85),
                    size: new Vector2(64, 16)
                },
                text: "Settings",
                callback: () => {
                    SPGameManager.Singleton.SetState(SPGameState.HowToPlay);
                }
            },
        ],

        [SPGameState.HowToPlay]: [
            {
                shape: {
                    center: new Vector2(0.5, 0.9),
                    size: new Vector2(64, 16)
                },
                text: "Back",
                callback: () => {
                    SPGameManager.Singleton.SetState(SPGameState.Title);
                }
            },
            {
                shape: {
                    center: new Vector2(0.5, 0.85),
                    size: new Vector2(64, 16)
                },
                //@ts-ignore
                text: `Toggle Bloom`,
                callback: (engine?: Engine) => {
                    //@ts-ignore
                    engine.bloom = !engine.bloom;
                    //@ts-ignore
                    localStorage.setItem("bBloomEnabled", engine.bloom.toString());
                }
            },
            {
                shape: {
                    center: new Vector2(0.5, 0.8),
                    size: new Vector2(64, 16)
                },
                text: "Fullscreen",
                callback: () => {
                    document.body.requestFullscreen();
                }
            },
        ],
        [SPGameState.InGame]: [],
        [SPGameState.GameOver]: [
            {
                text: "Main Menu",
                shape: {
                    center: new Vector2(0.5, 0.9),
                    size: new Vector2(64,16)
                },
                callback: () => {
                    window.location.reload();
                }
            }
        ],
        [SPGameState.AbilityPick]: [],
        [SPGameState.Pause]: [],
    };

    Init(): void {
        if (SPGameManager.Singleton == undefined) {
            this.gameObject.layer = 1;
            SPGameManager.Singleton = this;
        } else if (!SPGameManager.Singleton.Equals(this)) {
            this.gameObject.RemoveScript(this.id);
        }
    }

    private _state: SPGameState = SPGameState.Title;

    get state() {return this._state;}
    get isInGame() {return this._state === SPGameState.InGame;}
    get isInGameOrGameOver() {return this._state === SPGameState.InGame || this._state === SPGameState.GameOver;}

    abilityIconsImg = document.querySelector("img#ability_icons") as HTMLImageElement;
    controlsImg = document.querySelector("img#controls_img") as HTMLImageElement;
    titleImg = document.querySelector("img#title_img") as HTMLImageElement;

    availablePerks: Perk[] = [];
    crosshairSize = 4;

    takenAbilities: Record<number, Perk> = {};
    takenAbilityIds: string[] = [];

    abilityPickWaitTimestamp = 0;
    // helpVisible = false;
    helpEl: HTMLDivElement|undefined;


    bWolfKilledEarly = false;

    titleText = {
        text: "",
        fadeIn: 1,
        stay: 1,
        fadeOut: 1,
        timer: 0,
        state: 0,
        alpha: 0
    }

    SetTitleText(text: string, options: {
        fadeIn?: number;
        stay?: number;
        fadeOut?: number;
    } = {}) {
        Object.assign(this.titleText, options);
        this.titleText.text =  text;
        this.titleText.timer = this.titleText.fadeIn;
        this.titleText.state = 0;

    }

    GeneratePerkHelpHTML() {
        let perkhtml = `<p>Level</p>
<p>Icon</p>
<p>Name</p>
<p>Type</p>
<p>Description</p>`;

        for (const sLv in this.takenAbilities) {
            const perk = this.takenAbilities[sLv];

            const c: HTMLCanvasElement = document.createElement("canvas");
            c.width = 32;
            c.height = 32;
            const ct: CanvasRenderingContext2D = c.getContext("2d") as CanvasRenderingContext2D;
            this.DrawAbilityIcon(perk.icon, Vector2.zero, ct);
            const src = c.toDataURL();

            perkhtml += `<p>Lv.${sLv}</p>
<p><img class="perk-img" src="${src}" alt="${perk.name}" /></p>
<p>${perk.name}</p>
<p>${perk.type}</p>
<p>${perk.description}</p>`;
        }

        return `
<p>Perk list</p>
<div class="perk-grid">
    ${perkhtml}
</div>`;

    }

    DrawAbilityIcon(index: number, position: Vector2, ctx?: CanvasRenderingContext2D) {
        const sx = (index % (this.abilityIconsImg.width/32)) * 32;
        const sy = Math.floor(index/(this.abilityIconsImg.width/32)) * 32;
        if (ctx == undefined) this.ctx.drawImage(this.abilityIconsImg, sx, sy, 32, 32, position.x - 16, position.y - 16, 32, 32);
        else ctx.drawImage(this.abilityIconsImg, sx, sy, 32, 32, 0, 0, 32, 32);
    }

    SetState(state: SPGameState) {
        switch (state) {
            case SPGameState.Title:
                
                break;
            case SPGameState.AbilityPick:
                Time.scale = 0;
                this.abilityPickWaitTimestamp = this.engine.GetRealTime();
                this.availablePerks = GetAbilitySelection(SPPlayer.Singleton.level);
                break;
            case SPGameState.GameOver:
                
                break;
            case SPGameState.HowToPlay:
                
                break;
            case SPGameState.InGame:
                Time.scale = 1;
                break;
            case SPGameState.Pause:
                Time.scale = 0;
                break;
        
            default:
                break;
        }

        this._state = state;
    }

    bgMusic: HTMLAudioElement|undefined;
    currentlyPlaying: SoundByte = SoundByte.TitleSong;
    audioCtx: AudioContext|undefined;
    StartGame() {
        this.engine.RemoveGameObject("spawn-manager");
        this.engine.NewGameObjectWithScript(SPSpawnManager, {
            id: "spawn-manager"
        });
        this.SetState(SPGameState.AbilityPick);
        // this._state = SPGameState.InGame;

        
    }

    StartBGM(startTime = 0, volume = 1) {
        if (this.bgMusic) {
            this.bgMusic.currentTime = startTime;
            this.bgMusic.volume = volume;
            this.bgMusic.play();
        }
    }

    ChangeBGM(song: SoundByte, startTime = 0, volume = 1) {
        this.StopBGM()
            .then(() => {
                if (!this.bgMusic) return;

                this.bgMusic.src = song;
                this.currentlyPlaying = song;
                this.StartBGM(startTime, volume);
            })
            .catch(console.error);

    }

    StopBGM() {
        return new Promise((resolve, reject) => {
            if (!this.bgMusic) return resolve(false);

            const r = () => {
                if (!this.bgMusic) return resolve(false);

                this.bgMusic.volume = clamp01(this.bgMusic.volume - 0.33 * Time.deltaTime);

                if (this.bgMusic.volume === 0) {
                    this.bgMusic.pause();
                    return resolve(true);
                }

                setTimeout(() => {
                    r();
                }, Time.deltaTime * 1000);
            }

            r();
        });
    }

    bi = -1;
    lastBI = -1;
    AudioUpdate() {
        if (!this.bgMusic) return;
        
        const bpm  = 110;
        const bps = bpm/60;
        const bdelta = 1/bps;
        const t = this.bgMusic.currentTime/this.bgMusic.duration;
        this.bi = Math.floor(this.bgMusic.currentTime/bdelta);

        if (this.bi !== this.lastBI) {
            SoundManager.Singleton.listener.send("onBeat", undefined);
            
            this.lastBI = this.bi;
        }
    }

    TitleUpdate() {}

    
    AbilityPickUpdate() {

        if (this.takenAbilities[SPPlayer.Singleton.level]) {
            const perk = this.takenAbilities[SPPlayer.Singleton.level];
            perk.callback();
            this.SetState(SPGameState.InGame)
            return;
        }

        if (this.engine.GetRealTime() - this.abilityPickWaitTimestamp < 1) return;

        const winSize = new Vector2(this.engine.width - 10, this.engine.height - 10);
        const drawPos: Vector2 = new Vector2(winSize.x/(this.availablePerks.length || 1)/2 + 4, this.engine.center.y);

        for (let i = 0; i < this.availablePerks.length; i++) {
            const perk = this.availablePerks[i];
            const btn: Shapes.Rect = {
                center: drawPos,
                size: winSize.multiply(new Vector2(1/(this.availablePerks.length || 1), 1)).sub(new Vector2(12, 0))
            };

            if (Physics.PointInRect(Input.mousePosition, btn) && Input.GetMouseButtonUp(0)) {
                perk.callback();
                this.takenAbilities[SPPlayer.Singleton.level] = perk;
                this.takenAbilityIds.push(perk.id);
                this.SetState(SPGameState.InGame);

                if (this.helpEl) {
                    this.helpEl.innerHTML = this.GeneratePerkHelpHTML();
                }
            }

            drawPos.x += winSize.x/(this.availablePerks.length || 1);
        }
    }

    GameOverUpdate() {}

    HowToPlayUpdate() {}

    InGameUpdate() {}

    PauseUpdate() {
        if (DialogueManager.Singleton.hasChoices) {
            const choices = DialogueManager.Singleton.GetChoices()
            const btnSize = new Vector2(this.engine.width - 128, 32);
            const drawPos = this.engine.center.clone();
            drawPos.y -= 64;

            for (let i = 0; i < choices.length; i++) {
                const choice = choices[i];

                if (Input.GetMouseButtonUp(0) && Physics.PointInRect(
                    Input.mousePosition,
                    {
                        center: drawPos,
                        size: btnSize
                    }
                )) {
                    this.SetState(SPGameState.InGame);
                    DialogueManager.Singleton.ClearChoices();
                    choice.callback();
                }

                drawPos.y += 64;
            }
        }
    }

    Update(): void {
        switch (this._state) {
            case SPGameState.Title:
                this.TitleUpdate();
                break;
            case SPGameState.AbilityPick:
                this.AbilityPickUpdate();
                break;
            case SPGameState.GameOver:
                this.GameOverUpdate();
                break;
            case SPGameState.HowToPlay:
                this.HowToPlayUpdate();
                break;
            case SPGameState.InGame:
                this.InGameUpdate();
                break;
            case SPGameState.Pause:
                this.PauseUpdate();
                break;
        
            default:
                break;
        }

        this.AudioUpdate();

        if (!this.bgMusic && Input.GetMouseButtonDown(0)) {
            this.audioCtx = new AudioContext();
            this.bgMusic = document.createElement("audio");
            const src = this.audioCtx.createMediaElementSource(this.bgMusic);
            src.connect(this.audioCtx.destination);
            this.bgMusic.volume = SoundManager.Singleton.masterVolume * SoundManager.Singleton.musicVolume;
            this.bgMusic.loop = true;
            this.bgMusic.src = "./audio/music/title_music.ogg";
            this.bgMusic.play();
        }

        for (const btn of SPGameManager.buttons[this._state]) {
            const btnPos = btn.shape.center.multiply(new Vector2(this.engine.width, this.engine.height));
            if (Physics.PointInRect(Input.mousePosition, {
                center: btnPos,
                size: btn.shape.size
            }) && Input.GetMouseButtonUp(0)) {
                btn.callback(this.engine);
            }
        }

        for (const slider of SPGameManager.sliders[this._state]) {
            const pos = slider.position.multiply(new Vector2(this.engine.width, this.engine.height));
            
            if (Physics.PointInRect(Input.mousePosition, {
                center: pos,
                size: new Vector2(100, 16)
            }) && Input.GetMouseButton(0)) {
                const v = clamp(Input.mousePosition.x - (pos.x - 50), 0, 100);
                const t = clamp01(v/100);
                slider.set(t);
            }
        }

        if (this.titleText.state < 3) {
            if (this.titleText.timer > 0) {
                this.titleText.timer -= Time.deltaTime;

                if (this.titleText.state === 0) {
                    this.titleText.alpha = clamp01(1 - this.titleText.timer / this.titleText.fadeIn);
                }
                if (this.titleText.state === 2) {
                    this.titleText.alpha = clamp01(this.titleText.timer / this.titleText.fadeOut);
                }

                if (this.titleText.timer <= 0) {

                    switch (++this.titleText.state) {
                        case 1:
                            this.titleText.timer = this.titleText.stay;
                            break;
                        case 2:
                            this.titleText.timer = this.titleText.fadeOut;
                            break;
                    
                        default:
                            this.titleText.timer = 0;
                            break;
                    }
                }
            }
        }

        if (Input.GetKeyDown(KeyCode.Backspace) && Input.GetKey(KeyCode.ControlLeft) && Input.GetKey(KeyCode.ShiftLeft)) {
            this.engine.drawGizmos = !this.engine.drawGizmos;
        }

        
        if (Input.GetKeyDown(KeyCode.F1)) {
            if (this.helpEl == undefined) {
                this.helpEl = document.createElement("div");
                this.helpEl.id = "help-window";
                this.helpEl.innerHTML = this.GeneratePerkHelpHTML();

                document.body.appendChild(this.helpEl);
            } else {
                this.helpEl.remove();
                this.helpEl = undefined;
            }
        }
    }

    DrawTitleScreen() {

        this.ctx.fillStyle = this.engine.viewport.fColor;
        this.ctx.fillRect(0, 0, this.engine.width, this.engine.height);
        this.ctx.fillStyle = this.engine.viewport.bgColor;
        this.ctx.fillRect(2, 2, this.engine.width - 4, this.engine.height - 4);

        this.ctx.drawImage(this.titleImg, this.engine.center.x - this.titleImg.width/2, this.engine.center.y - this.titleImg.height/2, this.titleImg.width, this.titleImg.height);

    }

    DrawAbilityPickScreen() {

        if (this.takenAbilities[SPPlayer.Singleton.level]) {
            return;
        }

        this.ctx.fillStyle = this.engine.viewport.fColor;
        this.ctx.fillRect(0, 0, this.engine.width, this.engine.height);
        this.ctx.fillStyle = this.engine.viewport.bgColor;
        this.ctx.fillRect(2, 2, this.engine.width - 4, this.engine.height - 4);

        const winSize = new Vector2(this.engine.width - 10, this.engine.height - 10);
        const drawPos: Vector2 = new Vector2(winSize.x/(this.availablePerks.length || 1)/2 + 4, this.engine.center.y);
        
        for (let i = 0; i < this.availablePerks.length; i++) {
            const perk = this.availablePerks[i];
            const btn = {
                center: drawPos,
                size: winSize.multiply(new Vector2(1/(this.availablePerks.length || 1), 1)).sub(new Vector2(12, 0))
            };

            const fcol = perk.type === PerkType.Weapon ? '#ffff55' : perk.type === PerkType.Ability ? '#55ff55' : '#5555ff';

            this.ctx.fillStyle = fcol;
            this.engine.FillRectScreenFromCenter(btn.center, btn.size);
            this.ctx.fillStyle = this.engine.viewport.bgColor;
            this.engine.FillRectScreenFromCenter(btn.center, btn.size.sub(new Vector2(2, 2)));

            if (Physics.PointInRect(Input.mousePosition, btn)) {
                this.ctx.fillStyle = fcol + (Input.GetMouseButton(0) ? '30' : '10');
                this.engine.FillRectScreenFromCenter(btn.center, btn.size.sub(new Vector2(2, 2)));
            }

            this.engine.drawTexts([
                perk.name,
                perk.type,
                "",
                "",
                "",
                "",
                "",
                perk.description,
            ], drawPos, {
                align: 'center',
                baseline: 'middle',
                color: fcol
            });

            this.engine.drawTexts([
                perk.type
            ], drawPos.add(new Vector2(-btn.size.x/2 + 4, btn.size.y/2)), {
                align: 'left',
                baseline: 'bottom',
                color: fcol + "20",
                size: 32
            });
            
            if (perk.type !== PerkType.Ability) {
                this.ctx.save();
                this.ctx.filter = `hue-rotate(${perk.type === PerkType.Weapon ? -50 : 90}deg)`;
                this.DrawAbilityIcon(perk.icon, drawPos.sub(new Vector2(0, 16)));
                this.ctx.restore();
            } else {
                this.DrawAbilityIcon(perk.icon, drawPos.sub(new Vector2(0, 16)));
            }

            

            if (Physics.PointInRect(Input.mousePosition, btn)) {
                this.ctx.fillStyle = fcol + (Input.GetMouseButton(0) ? '30' : '10');
                this.engine.FillRectScreenFromCenter(btn.center, btn.size.sub(new Vector2(2, 2)));
            }

            drawPos.x += winSize.x/(this.availablePerks.length || 1);
        }
    }

    DrawGameOverScreen() {}

    DrawHowToPlayScreen() {
        this.ctx.fillStyle = this.engine.viewport.fColor;
        this.ctx.fillRect(0, 0, this.engine.width, this.engine.height);
        this.ctx.fillStyle = this.engine.viewport.bgColor;
        this.ctx.fillRect(2, 2, this.engine.width - 4, this.engine.height - 4);

        const size = FitRectToScreen(this.controlsImg.width, this.controlsImg.height, this.engine.width - 256, this.engine.height - 256);

        this.ctx.drawImage(this.controlsImg, this.engine.center.x - size.w/2, this.engine.center.y - size.h/2, size.w, size.h);
    }

    DrawInGameScreen() {}

    DrawPauseScreen() {

        if (DialogueManager.Singleton.hasChoices) {
            const choices = DialogueManager.Singleton.GetChoices()
            const btnSize = new Vector2(this.engine.width - 128, 32);
            const drawPos = this.engine.center.clone();
            drawPos.y -= 64;

            for (let i = 0; i < choices.length; i++) {
                const choice = choices[i];

                const isMouseOverChoice = Physics.PointInRect(Input.mousePosition, {
                    center: drawPos,
                    size: btnSize
                });

                this.ctx.strokeStyle = '#55ff55';
                this.ctx.fillStyle = isMouseOverChoice ? (Input.GetMouseButton(0) ? '#55ff5555' : '#55ff5520') : '#00000080';
                this.ctx.lineWidth = 1;

                this.engine.FillRectScreenFromCenter(drawPos, btnSize);
                this.engine.StrokeRectScreenFromCenter(drawPos, btnSize);
                this.engine.drawTexts([choice.text], drawPos, {
                    align: 'center',
                    baseline: 'middle',
                    color: 'white',
                    size: 16
                });

                drawPos.y += 64;
            }
        }

    }

    Draw(): void {
        switch (this._state) {
            case SPGameState.Title:
                this.DrawTitleScreen();
                break;
            case SPGameState.AbilityPick:
                this.DrawAbilityPickScreen();
                break;
            case SPGameState.GameOver:
                this.DrawGameOverScreen();
                break;
            case SPGameState.HowToPlay:
                this.DrawHowToPlayScreen();
                break;
            case SPGameState.InGame:
                this.DrawInGameScreen();
                break;
            case SPGameState.Pause:
                this.DrawPauseScreen();
                break;
        
            default:
                break;
        }

        for (const btn of SPGameManager.buttons[this._state]) {
            this.ctx.fillStyle = this.engine.viewport.fColor;
            const btnPos = btn.shape.center.multiply(new Vector2(this.engine.width, this.engine.height));
            this.engine.FillRectScreenFromCenter(btnPos, btn.shape.size.add(new Vector2(2, 2)));
            this.ctx.fillStyle = this.engine.viewport.bgColor;
            this.engine.FillRectScreenFromCenter(btnPos, btn.shape.size);

            if (Physics.PointInRect(Input.mousePosition, {center: btnPos, size: btn.shape.size})) {
                this.ctx.fillStyle = this.engine.viewport.fColor + (Input.GetMouseButton(0) ? '30' : '10');
                // this.ctx.fillStyle = 'white';
                this.engine.FillRectScreenFromCenter(btnPos, btn.shape.size);
            }
            
            this.engine.drawTexts([btn.text], btnPos, {
                align: 'center',
                baseline: 'middle',
                color: this.engine.viewport.fColor
            });
        }

        for (const slider of SPGameManager.sliders[this._state]) {
            const pos = slider.position.multiply(new Vector2(this.engine.width, this.engine.height)).sub(new Vector2(50, 8));
            this.ctx.strokeStyle = '#55ff55';
            this.ctx.fillStyle = '#55ff5555';
            this.ctx.lineWidth = 1;
            this.ctx.strokeRect(pos.x, pos.y, 100, 16);
            this.ctx.fillRect(pos.x, pos.y, 100 * slider.get(), 16);
            this.engine.drawTexts([slider.label], pos, {
                align: 'left',
                baseline: 'bottom',
                color: 'white'
            });
        }

        if (this.state !== SPGameState.InGame) {
            this.ctx.strokeStyle = this.engine.viewport.fColor;
            this.ctx.lineWidth = 1;
            this.engine.strokeShapeScreenPos(
                Input.mousePosition,
                Input.mousePosition.add(Vector2.down.scale(16)),
                Input.mousePosition.add(Vector2.down.rotate(-Math.PI/8).scale(12)),
                Input.mousePosition.add(Vector2.down.rotate(-Math.PI/4).scale(16)),
            );
        } else {
            this.ctx.strokeStyle = this.engine.viewport.fColor;
            this.ctx.lineWidth = 1;
            this.ctx.fillStyle = this.engine.viewport.fColor;
                    this.engine.StrokeCircle(
                        this.engine.toWorldPosition(Input.mousePosition),
                        this.crosshairSize
                    );
                    this.engine.FillRectFromCenter(this.engine.mouseWorldPosition.add(Vector2.up.scale(this.crosshairSize)), new Vector2(this.crosshairSize/4, this.crosshairSize));
                    this.engine.FillRectFromCenter(this.engine.mouseWorldPosition.add(Vector2.down.scale(this.crosshairSize)), new Vector2(this.crosshairSize/4, this.crosshairSize));
                    this.engine.FillRectFromCenter(this.engine.mouseWorldPosition.add(Vector2.left.scale(this.crosshairSize)), new Vector2(this.crosshairSize, this.crosshairSize/4));
                    this.engine.FillRectFromCenter(this.engine.mouseWorldPosition.add(Vector2.right.scale(this.crosshairSize)), new Vector2(this.crosshairSize, this.crosshairSize/4));
        }

        this.engine.drawTexts(this.titleText.text.split("\n"), this.engine.center, {
            align: 'center',
            baseline: 'middle',
            color: new Color(80,255,80).toHexWithAlpha(this.titleText.alpha),
        });
    }

}